import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Grafana Faro React統合
 *
 * ReactアプリケーションにFaroErrorBoundaryを提供する。
 *
 * @graph-stack faro
 * @graph-domain Infrastructure
 * @graph-business Faro React統合コンポーネント群。ErrorBoundary/ProfilerWrapper/withProfiler HOCによるフロントエンドエラー監視+パフォーマンス計測
 */
import { FaroErrorBoundary as BaseFaroErrorBoundary, FaroRoutes, ReactIntegration, } from '@grafana/faro-react';
// ============================================================
// Components
// ============================================================
/**
 * エラーフォールバックのデフォルトUI
 * @graph-connects none
 */
function DefaultErrorFallback(error) {
    return (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
        }, children: [
            _jsx("h1", { style: { color: '#dc3545', marginBottom: '1rem' }, children: "\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F" }), _jsx("p", { style: { color: '#6c757d', marginBottom: '1rem' }, children: "\u7533\u3057\u8A33\u3042\u308A\u307E\u305B\u3093\u304C\u3001\u4E88\u671F\u3057\u306A\u3044\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F\u3002" }), _jsx("pre", { style: {
                    backgroundColor: '#fff',
                    padding: '1rem',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    maxWidth: '100%',
                    overflow: 'auto',
                    fontSize: '0.875rem',
                    color: '#dc3545',
                }, children: error.message }), _jsx("button", { onClick: () => {
                    window.location.reload();
                }, style: {
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                }, children: "\u30DA\u30FC\u30B8\u3092\u518D\u8AAD\u307F\u8FBC\u307F" })
        ] }));
}
/**
 * Faroと統合されたエラーバウンダリ
 *
 * エラーをキャッチしてGrafana Cloudに送信し、フォールバックUIを表示する。
 *
 * @example
 * ```tsx
 * import { FaroErrorBoundary } from '@cortex/faro/react';
 *
 * function App() {
 *   return (
 *     <FaroErrorBoundary>
 *       <RouterProvider router={router} />
 *     </FaroErrorBoundary>
 *   );
 * }
 * ```
 * @graph-connects DefaultErrorFallback [calls] デフォルトフォールバックUI
 */
export function FaroErrorBoundary({ children, fallback }) {
    const fallbackFn = fallback ?? DefaultErrorFallback;
    return _jsx(BaseFaroErrorBoundary, { fallback: fallbackFn, children: children });
}
// Re-export useful components and utilities
export { FaroRoutes, ReactIntegration };
/**
 * パフォーマンス計測のラッパーコンポーネント
 *
 * コンポーネントのレンダリングパフォーマンスを計測する。
 *
 * @example
 * ```tsx
 * import { ProfilerWrapper } from '@cortex/faro/react';
 *
 * function MyPage() {
 *   return (
 *     <ProfilerWrapper name="MyPage">
 *       <ExpensiveComponent />
 *     </ProfilerWrapper>
 *   );
 * }
 * ```
 * @graph-connects none
 */
export function ProfilerWrapper({ children, name, }) {
    // Faroのプロファイラーはマウント時にパフォーマンスを計測
    // シンプルなラッパーとして実装
    return _jsx("div", { "data-faro-profiler": name, children: children });
}
/**
 * コンポーネントにFaroプロファイラーを付与するHOC
 *
 * @example
 * ```tsx
 * import { withProfiler } from '@cortex/faro/react';
 *
 * const MyComponent = withProfiler(function MyComponent() {
 *   return <div>...</div>;
 * }, 'MyComponent');
 * ```
 * @graph-connects ProfilerWrapper [calls] パフォーマンス計測ラッパー
 */
export function withProfiler(Component, name) {
    const componentName = name ?? Component.displayName ?? Component.name;
    const WrappedComponent = (props) => {
        return (_jsx(ProfilerWrapper, { name: componentName, children: _jsx(Component, { ...props }) }));
    };
    WrappedComponent.displayName = `withProfiler(${componentName})`;
    return WrappedComponent;
}
//# sourceMappingURL=react.js.map